import { isPlatformBrowser } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, effect, ElementRef, inject, NgZone, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EMPTY, finalize, map, Observable, startWith, Subscription, switchMap, tap } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { ChatService } from '../../services/chat/chat.service';
import { ToasterService } from '../../shared/services/toaster.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AskQuestionModel, chat, messages } from '../../model/chat.model';
import { BlockElementsComponent } from '../answer/block-elements/block-elements.component';
import { FormatChatPipe } from '../../pipes/format-chat/format-chat.pipe';
import { RuleAssistModalComponent } from '../../shared/components/rule-assist-modal/rule-assist-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SharedService } from '../../shared/services/shared.service';
import { v4 as uuidv4 } from 'uuid';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MediaService } from '../../services/media.service';
import { SourceBlockComponent } from '../../shared/components/sources/source-block/source-block.component';
import { SourceModalComponent } from '../../shared/components/sources/block-modal/block-modal.component';
import { SiteLogoService } from '../../shared/services/site-logo.service';
import { BackButton } from '../../model/shared.model';
@Component({
  selector: 'app-chat-box',
  imports: [
    FormsModule,
    BlockElementsComponent,
    SourceBlockComponent,
    SourceModalComponent,
    FormatChatPipe,
    TranslatePipe,
    RuleAssistModalComponent,
    RouterLink
  ],
  providers: [TranslatePipe],
  templateUrl: './chat-box.component.html',
  styleUrl: './chat-box.component.scss'
})
export class ChatBoxComponent implements OnInit, AfterViewChecked {
  private _subscription: Subscription = new Subscription();
  private _ngZone = inject(NgZone);
  private _chatService = inject(ChatService);
  private _toaster = inject(ToasterService);
  private _translatePipe = inject(TranslatePipe);
  private _authService = inject(AuthService);
  private _changeDetect = inject(ChangeDetectorRef);
  private _modalService = inject(NgbModal);
  private _sharedService = inject(SharedService);
  private _router = inject(Router);
  private _mediaService = inject(MediaService);
  private _activatedRoute = inject(ActivatedRoute);
  private _siteLogoService = inject(SiteLogoService);
  private lastCookieSetTime: number = 0; // Timestamp of the last cookie set
  private cookieExpiryDuration: number = 90 * 1000; // 1.5 minutes in milliseconds
  private ruleAssistModalRef!: NgbModalRef;
  @ViewChildren('questionHeader') headers!: QueryList<ElementRef>;
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  chatPrompt = '';
  processing: boolean = false;
  loopMessages: chat[] = [];
  receivingMessage = false;
  textOnly = true;
  answerGenerating = false;
  chatId = '';
  isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  followUpQuestion = '';
  medias: any[] = [];
  isMyBag = false;
  messageSubscribed = false;
  processingText = this._translatePipe.transform('Generating...');
  private buffer = '';
  loading = false;
  answerProcessing = false;
  ruleAssistQuestionObject!: chat;
  answerListening = false;
  audioAnswerLoading = false;
  isSignedIn = false;
  currentChatPage = 1;
  isNewSharedChat = false;
  showLoadMoreBtn = false;
  loadingMoreHistory = false;
  continueChatProgress = false;
  chatLoading = false;
  question: AskQuestionModel = new AskQuestionModel();
  isSharedChat = false;
  sharedChatTriggered = false;

  @ViewChild('equalizerCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('audioRef') audioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('chatTextarea') chatTextareaRef!: ElementRef<HTMLTextAreaElement>;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  isRecording: boolean = false;
  recordingStatus: string = 'Not recording';
  base64Audio: string | null = null;
  showEqualizer = false;
  recordingFinished = false;
  mediaElementSource: MediaElementAudioSourceNode | null = null;
  audioContext!: AudioContext | null;
  analyser!: AnalyserNode;
  dataArray: any;
  animationFrameId!: any;
  isPlaying = false;
  audioUrl: string | null = null;
  audioBlob: Blob | undefined;
  totalMessages = 0;
  openChatBox = false;
  showSourceModal = false;
  selectedSources: any[] = [];
  hideLoadingText = false;
  prevButtonAction = new BackButton();
  isAudioPlaying = false;
  submittingAudio = false;

  constructor() {
    effect(() => {
      const buttonAction = this._sharedService.backButtonAction();
      if (buttonAction && buttonAction.action === 'chatBox') {
        this.closeChatPop();
        setTimeout(() => {
          this._sharedService.backButton.set(this.prevButtonAction);
        }, 100);
      }
    });

    effect(() => {
      this.isSignedIn = this._sharedService.loggedIn();
      if (!this.isSharedChat && this.isSignedIn){
        this.getLatestChatId();
      }
      if(this.isSignedIn && this.isBrowser){
        this.setCookieIfNeeded().subscribe({
          next: () => {
            this._ngZone.runOutsideAngular(() => {
              this._chatService.onListenWebsocket()
                .then(() => { })
                .catch((error) => {
                  this._toaster.error(this._translatePipe.transform("SocketConnectionError"),
                    this._translatePipe.transform("Error"));
                });
            })
          }
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      // this._chatService.onListenWebsocket();
    this._activatedRoute.queryParams.subscribe(params => {
      // this.testing = JSON.stringify(params);
      if (params['id']) {
        this.chatId = params['id'];
        if (params['regenerate'] == 'true') {
          this._sharedService.question.subscribe(res => {
            this.loopMessages.push({ question: res.textQuestion, answer: '', loading: true, id: '' });
            this.askQuestion(res, true);
            this.getMessages();
          })
        } else {
          this.chatLoading = true;
          this.getMessagesFromId();
        }
      } else if (params['sharedId']) {
        this.chatLoading = true;
        this.chatId = params['sharedId'];
        this.isSharedChat = true;
        if (!this.sharedChatTriggered) {
          this.getMessagesFromSharedId();
        }
        // this.clearQueryParams();
      } else {
        if (params['question']) {
          this.question.textQuestion = params['question'];
          this.answerProcessing = true;
          this.startChat(this.question);
          this.getMessages();
        } else if (params['myBag']) {
          this.isMyBag = true;
          this.question.textQuestion = 'My Bag Analysis';
          this.question.processType = 'QA-MY-BAG';
          this.loopMessages.push({ question: this.question.textQuestion, answer: '', loading: true, id: '' });
          // this.startChat(this.question, false);
          this._chatService.askQuestionV2(this.question);
          this.answerProcessing = true;
          this.getMessages();
        } else {
          // if(this.previousUrl?.includes("discover")){
          //   this._sharedService.articleData$.subscribe(res => {
          //     this.loopMessages.push({
          //       question: res.title,
          //       answer: res.description,
          //       loading: false,
          //       id: res.id
          //     })
          //   })
          // } else{
          this._subscription.add(
            this._sharedService.question.subscribe(res => {
              if (res && res.textQuestion) {
                this.question = res; 
                if (this.question.audioQuestion || this.question.mediaKeys.length) {
                  this.textOnly = false;
                }
                this.chatPrompt = res.textQuestion;
                this.chatLoading = true;
                // Only proceed if user is logged in
                if (this.isSignedIn) {
                  this.getChatResponse(res.separation);
                } else {
                  // Clear the question and redirect to login
                  this.nonLoggedInToaster();
                }
              }
            })
          );

          //region Test question
          // if (this.isBrowser) {
          //   const demoQuestion = new AskQuestionModel();
          //   demoQuestion.textQuestion = 'create an article about golf which includes three charts that is in different types and table of a famous player recent scores';
          //   this.question = demoQuestion;
          //   if (this.question.audioQuestion || this.question.mediaKeys.length) {
          //     this.textOnly = false;
          //   }
          //   this.startChat(this.question);
          //   this.answerProcessing = true;
          //   this.getMessages();
          // }
          
          // this._sharedService.question.next(demoQuestion);
        }
      }
    });
    }

    this._subscription.add(
      this._chatService.audioEnded().subscribe(res => {
        if (res === true) {
          this.answerListening = false;
          this.loopMessages.map(item => {
            item.listening = false;
            item.loadingAudio = false; // Clear loading flag for all messages when audio ends
          });
        } else if (res === false) {
          this.audioAnswerLoading = false;
          // Clear loading flag for all messages when audio fails to load
          this.loopMessages.map(item => item.loadingAudio = false);
        }
        this._changeDetect.markForCheck();
      })
    );
    
  }

  getLatestChatId() {
    this._chatService.getLatestChatId().subscribe(res => {
      this.chatId = res.chatId;
      this.chatLoading = true;
      this.getMessagesFromId();
    });
  }

  getMessagesFromSharedId() {
    if (!this.sharedChatTriggered || this.loadingMoreHistory) {
      this.sharedChatTriggered = true;
      if (this.isBrowser) {
        // this._sharedService.loading.set(false);
      }
      this._chatService.getMessageFromSharedId(this.chatId, 10, this.currentChatPage).subscribe({
        next: ((res: messages) => {
          res.results.forEach(element => {
            const isJson = this.isJson(element.content);
            if (isJson) {
              element.contentBlocks = JSON.parse(element.content);
              element.isBlocks = true;
              element.content = '';
            } else {
              element.contentBlocks = [];
              element.isBlocks = false;
            }
          });
          this.answerProcessing = false;
          this.processMessages(res, true);
        })
      })
    }
  }

  getMessagesFromId() {
    this._chatService.getMessageFromId(this.chatId, 10, this.currentChatPage).subscribe({
      next: ((res: messages) => {
        this.totalMessages = res.totalPages;
        res.results.forEach(element => {
          this.totalMessages = res.totalResults;
          const isJson = this.isJson(element.content);
          if (isJson) {
            element.contentBlocks = JSON.parse(element.content);
            element.isBlocks = true;
          } else {
            element.contentBlocks = [];
            element.isBlocks = false;
          }
        });
        this.answerProcessing = false;
        this.processMessages(res);
      }),
      error: ((res: any) => {
        this._router.navigateByUrl('/');
      })
    })
  }

  showInputBox(){
    if (this.answerProcessing){
      return;
    }
    this.hideLoadingText = true;
    setTimeout(() => {
      if (this.chatTextareaRef && this.chatTextareaRef.nativeElement) {
        this.chatTextareaRef.nativeElement.focus();
      }
    }, 100);
    this._changeDetect.markForCheck();
  }

  hideProcessingText(){
    if (!this.chatPrompt.trim()){
      this.hideLoadingText = false;
    }
  }

  setOpenChatBox(){
    this.hideLoadingText = true;
    if (typeof window !== 'undefined' && window.innerWidth < 991 && !this.openChatBox) {
      document.body.classList.add('fixed');
      this.openChatBox = true;
      this.prevButtonAction = this._sharedService.backButton();
      this._sharedService.backButton.set({
        show: true,
        url: '/',
        action: 'chatBox',
        text: ''
      });
    }
  }

  autoResize(event: any) {
    const textarea = event.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
  }

  processMessages(res: messages, isShared = false) {
    if (res.type === 'NEW' || !isShared) {
      if (res.type === 'NEW') {
        this.isNewSharedChat = true;
      }
      if (res.totalPages > 1) {
        this.showLoadMoreBtn = true;
      }
      if (!res.results.length) {
        this.showLoadMoreBtn = false;
      }
      if (this.loadingMoreHistory) {
        res.results.filter(rs => rs.role === 'user').forEach((item, index) => {
          if (item.media.length) {
            const md = item.media.filter(mm => mm);
            this.getMediaUrl(md);
          }
        });
        res.results.filter(rs => rs.role === 'assistant').forEach((item, index) => {
          if (item.role === 'assistant') {
            const questionIndex = res.results.findIndex(lm => lm.id === item.questionRef);
            if (questionIndex !== -1) {
              this.loopMessages.unshift({
                answer: item.content,
                contentBlocks: item.contentBlocks,
                isBlocks: item.isBlocks,
                question: res.results[questionIndex].content,
                loading: false,
                id: res.results[questionIndex].id,
                route: item.route,
                sources: item.sources,
                answerId: item.id,
                reaction: item.reaction
              });
            }
          }
        });

        // res.results.forEach((item) => {
        //   if(item.role === 'assistant'){
        //     questionIndex = 0;
        //     this.loopMessages.unshift({ question:  '', answer: item.content, loading: false });           
        //   } else if (item.role === 'user'){
        //     this.loopMessages[questionIndex].question = item.content;
        //   }
        // });

        if (res.totalResults === (this.loopMessages.length * 2)) {
          this.showLoadMoreBtn = false;
        }
        this.loadingMoreHistory = false;
      } else {
        res.results = res.results.reverse();
        res.results.filter(rs => rs.role === 'user').forEach((item, index) => {
          if (item.media.length) {
            const md = item.media.filter(mm => mm);
            this.getMediaUrl(md);
          }
        });

        let previousRoute: string | undefined = undefined;
        res.results.filter(rs => rs.role === 'assistant').forEach((item, index) => {
          if (item.role === 'assistant') {
            const questionIndex = res.results.findIndex(lm => lm.id === item.questionRef);
            if (questionIndex !== -1) {
              const message:chat = {
                answer: item.content,
                contentBlocks: item.contentBlocks,
                isBlocks: item.isBlocks,
                question: res.results[questionIndex].content,
                loading: false,
                id: res.results[questionIndex].id,
                sources: item.sources,
                route: item.route,
                answerId: item.id,
                reaction: item.reaction
              };
              // 🆕 Add `separation` if the route has changed
              if (item.route !== previousRoute) {
                message.separation = item.route;
                previousRoute = item.route;
              }

              this.loopMessages.push(message);
            }
          }
        })

        // res.results.forEach((item, index) => {
        //   if(item.role === 'user'){
        //     index === 0 ? questionIndex = 0 : questionIndex++;
        //     this.loopMessages.push({ question:  item.content, answer: '', loading: false });       
        //   } else if (item.role === 'assistant'){
        //     this.loopMessages[questionIndex].answer = item.content;
        //   }
        // });

        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
        this._changeDetect.markForCheck();
      }
      if (this.continueChatProgress) {
        this.question.textQuestion = this.followUpQuestion;
        this.askQuestion(this.question);
        this.continueChatProgress = false;
      }
      this.loadingMoreHistory = false;
      this.chatLoading = false;
      this.getMessages();
      
    } else if (res.type === 'OWNER' || res.type === 'FOLLOWER') {
      this._router.navigate(['answer'], { queryParams: { id: res.chatId } });
    }
    this.updateSourceImage();
    this._changeDetect.markForCheck();
  }

  updateSourceImage() {
    // Flatten all sources from loopMessages
    const allSources = this.loopMessages.flatMap(msg => msg.sources || []);
  
    // Use service to refresh logos
    this._siteLogoService.refreshLogosForSources(allSources).subscribe({
      next: (updatedSources) => {
        // Build a Map of sourceId -> fresh image URL
        const sourceImageMap = new Map(
          updatedSources.map(source => [source._id, source.image])
        );
  
        // Update images in loopMessages
        this.loopMessages.forEach(res => {
          res.sources.forEach((source: any) => {
            source.image = sourceImageMap.get(source._id) || 'assets/logos/default.png';
          });
        });
      },
      error: (err) => {
        console.error('Failed to refresh source images', err);
        // Fallback: keep old images
      }
    });
  }
  

  continueChat(question: string) {
    if (question.trim()) {
      this._chatService.continueChat(this.chatId).subscribe({
        next: (res => {
          this.chatId = res.chatId;
          this.continueChatProgress = true;
          this._router.navigate(['answer'], { queryParams: { id: res.chatId } });
        })
      })
    } else {
      this._toaster.warning(this._translatePipe.transform("QuestionRequired"), this._translatePipe.transform("Warning"));
    }
  }

  getMediaUrl(ids: string[]) {
    this._mediaService.getMedia(ids).subscribe({
      next: (res => {
        this.medias = res;
      })
    })
  }

  isJson(content: any): boolean{
    let parsedContent: any;
    let isJson = false;

    try {
      const maybeJson = typeof content === 'string' ? JSON.parse(content) : content;
      if (Array.isArray(maybeJson)) {
        parsedContent = maybeJson;
        isJson = true;
      }
    } catch {
      // Not valid JSON, treat as markdown
      isJson = false;
    }

    return isJson;
  }

  closeChatPop() {
    this.openChatBox = false;
    document.body.classList.remove('fixed');
  }

  nonLoggedInToaster(){
    this.chatPrompt = '';
    // Clear the question from shared service to prevent infinite loops
    this._sharedService.setQuestion(new AskQuestionModel());
    this._toaster.warning('Please login to ask a question', this._translatePipe.transform("Warning"));
    this._router.navigate(['/login']);
  }

  getChatResponse(separation = '') {
    if (!this.isSignedIn){
      this.nonLoggedInToaster();
      this._router.navigate(['/login']);
      return;
    }
    if(this.audioBlob || this.chatPrompt.trim()){
      this.setOpenChatBox();
    }
    if(this.audioBlob){
      this.submitAudioQuestion();
      this.answerProcessing = true;
    } else if (this.chatPrompt.trim()) {
      this.answerProcessing = true;
      let questionModel = new AskQuestionModel();
      questionModel.textQuestion = this.chatPrompt;
      questionModel.separation = separation;
      this.chatPrompt = '';
      this.hideProcessingText();
      if(this.chatId){
        this.askQuestion(questionModel);
      } else {
        this.startChat(questionModel);
      }
      this.getMessages();
    }
  }
//region GET MESSAGE
  getMessages(): void {
    if (!this.messageSubscribed) {
      this.messageSubscribed = true;
      this._subscription.add(
        this._chatService.getMessages().subscribe(message => {
          if(message.source && message.source === 'discovery') {
            return;
          }
          if (message.event === 'ANSWER_CHAT_ID') {
            this.chatId = message.data;
          } else if (message.event === "GETTING CONTEXT") {
            this.processingText = this._translatePipe.transform('Generating...');
          } else if (message.event === 'STATUS' && message.data && message.data.message) {
            this.processingText = message.data.message;
          } else if (message.event === 'ANSWER_TEXT_CHUNK') {
            this.answerProcessing = true;
            if (!this.receivingMessage) {
              this.receivingMessage = true;
              this.buffer = '';
            }
            this.buffer += message.data;
            this.loading = false;
            this.loopMessages[this.loopMessages.length - 1].loading = false;
            this.loopMessages[this.loopMessages.length - 1].showProcessingText = false;
            this.updateMessages(this.buffer);
            this.scrollToBottom();
          } else if (message.event === "ANSWER_SOURCE") {
            this.loopMessages[this.loopMessages.length - 1].sources = message.data;
          } else if (message.event === "ROUTE_INFO") {
            this.loopMessages[this.loopMessages.length - 1].route = message.data;
            this.loopMessages[this.loopMessages.length - 1].separation = message.data;
          } else if (message.event === 'ANSWER_ERROR') {
            this._toaster.error(message.error, "Error");
          } else if (message.event === "ANSWER_OBJECT") {
            this.answerProcessing = true;
            this.loopMessages[this.loopMessages.length - 1].loading = false;
            this.loopMessages[this.loopMessages.length - 1].showProcessingText = false;
            this.loopMessages[this.loopMessages.length - 1].isBlocks = true;
            this.loopMessages[this.loopMessages.length - 1].answerProcessing = true;
            const lastMessage = this.loopMessages[this.loopMessages.length - 1];
            if (lastMessage.contentBlocks) {
              lastMessage.contentBlocks = [...lastMessage.contentBlocks, message.data]; // Replace array
              this.scrollToBottom();
            } else {
              lastMessage.contentBlocks = [message.data];
            }
            this.answerGenerating = false;
            this._changeDetect.markForCheck();
          } else if (message.event === 'QUESTION_TEXT') {
            this.chatPrompt = message.data.trim();
            this.showInputBox();
            this.submittingAudio = false;
            this.closeRecording();
            // this.getChatResponse('')
          } else if (message.event === 'ANSWER_END') {
            if (message.data && message.data.answerId) {
              this.loopMessages[this.loopMessages.length - 1].answerId = message.data.answerId;
              this.loopMessages[this.loopMessages.length - 1].id = message.data.questionId;
            }
            this.loopMessages[this.loopMessages.length - 1].answerProcessing = false;
            this.buffer = '';
            this.processingText = '';
            this.answerProcessing = false;
            this.scrollToBottom();
            this._changeDetect.markForCheck();
          }
        })
      );

      this._subscription.add(
        this._chatService.endMessages().subscribe(message => {
          this.buffer = '';
        })
      )
    }
  }

  private updateMessages(message: string): void {
    this.loopMessages[this.loopMessages.length - 1].answer = message;
    this.loopMessages[this.loopMessages.length - 1].loading = false;
    this.answerGenerating = false;
    this._changeDetect.markForCheck();
    // this.makeElementDraggable(this.draggable.nativeElement);
  }

  setCookieIfNeeded(): Observable<void> {
    if (this._authService.isSignedIn()) {
      return EMPTY.pipe(startWith(void 0)); // Ensure next() is triggered
    }

    const currentTime = Date.now();
    if (currentTime - this.lastCookieSetTime < this.cookieExpiryDuration) {
      return EMPTY.pipe(startWith(void 0)); // Ensure next() is triggered
    }

    return this._authService.setCookie().pipe(
      tap(() => {
        this.lastCookieSetTime = currentTime;
      }),
      map(() => void 0)
    );
  }

  hearAnswer(text: any, index: number) {
    if (this.isSignedIn) {
      let formattedText = '';
      if (this.loopMessages[index].isBlocks && this.loopMessages[index].contentBlocks) {
        formattedText = this.convertToSpeechText(this.loopMessages[index].contentBlocks);
      } else {
        formattedText = text;
      }
      this.audioAnswerLoading = true;
      this.loopMessages[index].listening = true;
      this.loopMessages[index].loadingAudio = true; // Set loading flag for this specific message
      this.answerListening = true;
      const id = uuidv4();
      this.loopMessages[index].refId = id;
      this._chatService.textToAudio(formattedText, id);
    } else {
      this._router.navigate(['/login']);
    }
  }

  stopListening(index: number) {
    if (this.loopMessages[index].refId) {
      this._chatService.stopThisAudio(this.loopMessages[index].refId);
      this.loopMessages[index].listening = false;
    }
  }

  convertToSpeechText(blocks: any[]): string {
    let speechText = '';

    for (const block of blocks) {
      switch (block.type) {
        case 'paragraph':
          speechText += `${block.text}\n\n`;
          break;

        case 'heading':
          speechText += `${block.text.toUpperCase()}\n\n`;
          break;

        case 'list':
          const listItems = block.items.map((item: any) =>
            `- ${item}`
          );
          speechText += listItems.join('\n') + '\n\n';
          break;

        case 'quote':
          speechText += `Quote: "${block.text}"`;
          if (block.author) {
            speechText += ` — ${block.author}`;
          }
          speechText += `\n\n`;
          break;

        case 'link':
          speechText += `${block.text}. You can visit the link at: ${block.url}\n\n`;
          break;

        case 'divider':
          speechText += `---\n\n`;
          break;

        case 'image':
          speechText += `An image is displayed: ${block.alt || 'No description provided.'}\n\n`;
          break;

        case 'table':
          speechText += `A table titled "${block.caption || 'Table'}" is displayed on the screen. Please refer to it for more details.\n\n`;
          break;

        case 'chart':
          speechText += `A chart titled "${block.title}" is displayed on the screen. Please refer to it for data insights.\n\n`;
          break;

        default:
          // fallback for unhandled types
          break;
      }
    }

    return speechText.trim();
  }

  startChat(questionModel: AskQuestionModel, apiCall = true): void {
    if (questionModel.textQuestion.trim() || questionModel.audioQuestion) {
      this.loopMessages.push({ question: questionModel.textQuestion, answer: '', loading: true, id: '' });
      if (this.textOnly && apiCall) {
        this._chatService.startChat(questionModel.textQuestion).subscribe((res) => {
          this.chatId = res.chatId;
          this.askQuestion(questionModel, true);
          this.receivingMessage = false; // Mark the end of receiving the previous message
        });
      } else {
        this.askQuestion(questionModel, true);
        this.receivingMessage = false; // Mark the end of receiving the previous message
      }
    } else {
      this._toaster.error(this._translatePipe.transform("QuestionRequired"), this._translatePipe.transform("Error"));
    }
  }

  askQuestion(question: AskQuestionModel, isFirst = false, regenerate = false) {
    if ((question.textQuestion.trim() && !this.answerGenerating) || question.audioQuestion) {
      this.answerGenerating = true;
      if (!isFirst) {
        this.loopMessages.push({ question: question.textQuestion, separation: question.separation, answer: '', loading: true, id: '', showProcessingText: true });
        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
        this._changeDetect.markForCheck();
      }
      this.followUpQuestion = '';
      if (this.textOnly && !this.isMyBag) {
        this._chatService.askQuestion(this.chatId, question.textQuestion, regenerate, question.behaviour);
      } else {
        this.medias = [];
        question.mediaKeys.forEach(media => {
          const item = {
            url: media.get.url,
            type: '',
            name: media.filename
          }
          if (media.mimetype.includes('image')) {
            item.type = 'image'
          } else if (media.mimetype.includes('video')) {
            item.type = 'video'
          } else {
            item.type = 'document';
          }
          this.medias.push(item);
        });
        if (this.isMyBag) {
          question.regenerate = regenerate;
          question.chatId = this.chatId;
        }
        this._chatService.askQuestionV2(question);
      }
      // if(!this.chatId){
      //   this._chatService.startChat(question.textQuestion).subscribe((res) => {
      //     this.chatId = res.chatId;
      //     this._chatService.askQuestion(this.chatId, question.textQuestion, regenerate);
      //     this.getMessages();
      //   })
      // } else{

      // }
    }

    // const messageBody = { chatId, question };
    // this.socket.emit('askQuestion', messageBody);
  }

  scrollToLastHeader() {
    if (this.headers && this.headers.last) {
      const headerElement = this.headers.last.nativeElement;
      headerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const containerElement = document.querySelector('.answer_cont');
        if (containerElement) {
          containerElement.scrollTop = headerElement.offsetTop;
        }
      }, 300);
    }
  }

  openRuleAssistModal(content: any, index: number) {
    this.ruleAssistQuestionObject = this.loopMessages[index];
    this.ruleAssistModalRef = this._modalService.open(content, {
      backdrop: 'static',
      centered: true,
      windowClass: 'modal-rule-assist'
    });
  }

  closeRuleAssistModal() {
    this.ruleAssistModalRef?.dismiss();
  }

  ngAfterViewChecked(){
    if (this.scrollContainer && this.isBrowser){
      
    }
  }


  private scrollToBottom(): void {
    if(this.isBrowser){
      const el = this.scrollContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  addReaction(reaction: string, index: number, messageId: any) {
    if(this.loopMessages[index].reaction === reaction){
      this.loopMessages[index].reaction = '';
      reaction = 'none';
    } else {
      this.loopMessages[index].reaction = reaction;
    }

    this._chatService.addReactionTotheMessage(messageId, reaction).subscribe(res => {res});
  }

  // voice to chat functions

  async startRecording() {
    this.chatPrompt = '';
    this.hideProcessingText();
  
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Setup MediaRecorder (optional for recording)
    this.mediaRecorder = new MediaRecorder(stream);
    this.audioChunks = [];
    this.base64Audio = null;

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.audioChunks.push(event.data);
    };

    this.mediaRecorder.onstop = () => {
      this.audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
      this.audioUrl = URL.createObjectURL(this.audioBlob);
      this.audioRef.nativeElement.src = this.audioUrl;

      const audio = this.audioRef.nativeElement;

      audio.src = this.audioUrl;
      audio.load();
      this._changeDetect.markForCheck();


      audio.addEventListener('canplaythrough', () => {
        this.recordingFinished = true;
      }, { once: true }); // <- This ensures it only runs once
      // this.convertToBase64(audioBlob);
      stream.getTracks().forEach(track => track.stop());
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(err => console.error('Error closing AudioContext:', err));
      }
      cancelAnimationFrame(this.animationFrameId);
    };

    this.mediaRecorder.start();
    this.isRecording = true;
    this.recordingStatus = 'Recording...';
    this.showEqualizer = true;

    // Setup Web Audio API
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 64;

    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    source.connect(this.analyser);

    this.animateEqualizer();
  }

  // Call this when user clicks play
  startPlaybackEqualizer() {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContext();
      this.mediaElementSource = null; // Reset, since context is new
    }

    const audioElement = this.audioRef.nativeElement;

    if (!this.mediaElementSource) {
      this.mediaElementSource = this.audioContext.createMediaElementSource(audioElement);

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 64;
      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      this.mediaElementSource.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);
    }

    this.animateEqualizer();
  }

  stopPlaybackEqualizer() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Optionally clear the canvas
    // const canvas = this.canvasRef.nativeElement;
    // const ctx = canvas.getContext('2d');
    // ctx!.clearRect(0, 0, canvas.width, canvas.height);
  }

  async onPlay() {
    this.startPlaybackEqualizer();
  
    const audio = this.audioRef.nativeElement;
  
    try {
      // Resume audio context if suspended
      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }
  
      audio.muted = false;
      audio.volume = 1;
  
      await audio.play();
    } catch (err) {
      console.error('Playback error:', err);
    }
  }

  onPause() {
    const audioElement = this.audioRef?.nativeElement;
    if (audioElement) {
      audioElement.pause();
      this.onPauseOrEnd();
    }
  }

  onAudioPlay() {
    this.isAudioPlaying = true;
    console.log(this.isAudioPlaying);
  }

  onPauseOrEnd() {
    this.isAudioPlaying = false;
    this.stopPlaybackEqualizer();
  }

  animateEqualizer() {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2;
    
    // Waveform visualization properties
    const barWidth = 3; // Width of each bar
    const barSpacing = 3; // Space between bars (same as bar width)
    const totalBarWidth = barWidth + barSpacing;
    const numBars = Math.floor(width / totalBarWidth);
    const maxBarHeight = height * 0.6; // Decreased height
    const minBarHeight = 2;
    const dotSpacing = 12; // Increased spacing between dots
    const dotSize = 3; // Increased dot width
    
    // Audio history for waveform
    const waveformData: number[] = new Array(numBars).fill(0);
    const audioLevels: number[] = new Array(numBars).fill(0); // Store raw audio levels
    
    // Animation timing control
    let frameCount = 0;
    const updateFrequency = 2; // Update every 2 frames (slightly slower)
    
    const draw = () => {
      this.animationFrameId = requestAnimationFrame(draw);
      
      frameCount++;
      
      // Update waveform data every frame for high sensitivity
      if (frameCount % updateFrequency === 0) {
        // Get audio frequency data
        this.analyser.getByteFrequencyData(this.dataArray);
        
        // Calculate average audio level
        const average = this.dataArray.reduce((sum: number, value: number) => sum + value, 0) / this.dataArray.length;
        const normalizedLevel = (average / 255);
        
        // Shift existing data to the left (like a scrolling waveform)
        for (let i = 0; i < waveformData.length - 1; i++) {
          waveformData[i] = waveformData[i + 1];
          audioLevels[i] = audioLevels[i + 1];
        }
        
        // Add new audio data to the right with slightly reduced height sensitivity
        const hasAudio = normalizedLevel > 0.005; // Lower threshold for detection
        const intensityMultiplier = 1.5; // Slightly reduced intensity
        const newBarHeight = hasAudio ? 
          minBarHeight + (Math.pow(normalizedLevel, 0.8) * (maxBarHeight - minBarHeight) * intensityMultiplier) : 
          0;
        
        waveformData[waveformData.length - 1] = Math.min(newBarHeight, maxBarHeight);
        audioLevels[audioLevels.length - 1] = normalizedLevel;
      }
      
      // Clear canvas
      ctx!.clearRect(0, 0, width, height);
      
      // Check if there's any recent audio activity (stricter check for transition)
      const recentAudioActivity = audioLevels.some(level => level > 0.005);
      const hasSignificantBars = waveformData.some(height => height > minBarHeight);
      
      if (recentAudioActivity || hasSignificantBars) {
        // Set drawing style for bars
        ctx!.fillStyle = '#000000';
        
        // Draw waveform bars with rounded edges
        for (let i = 0; i < numBars; i++) {
          const x = i * totalBarWidth;
          const barHeight = waveformData[i];
          
          if (barHeight > minBarHeight) {
            // Draw rounded vertical bar centered on centerY
            const radius = barWidth / 2; // Radius is half the bar width
            const barTop = centerY - barHeight / 2;
            const barBottom = centerY + barHeight / 2;
            
            ctx!.beginPath();
            ctx!.moveTo(x + radius, barTop);
            ctx!.lineTo(x + barWidth - radius, barTop);
            ctx!.arc(x + barWidth - radius, barTop + radius, radius, -Math.PI/2, 0);
            ctx!.lineTo(x + barWidth, barBottom - radius);
            ctx!.arc(x + barWidth - radius, barBottom - radius, radius, 0, Math.PI/2);
            ctx!.lineTo(x + radius, barBottom);
            ctx!.arc(x + radius, barBottom - radius, radius, Math.PI/2, Math.PI);
            ctx!.lineTo(x, barTop + radius);
            ctx!.arc(x + radius, barTop + radius, radius, Math.PI, -Math.PI/2);
            ctx!.closePath();
            ctx!.fill();
          } else {
            // Draw small dot when bar height is minimal
            ctx!.beginPath();
            ctx!.arc(x + barWidth/2, centerY, 1, 0, Math.PI * 2);
            ctx!.fill();
          }
        }
      } else {
        // Draw dotted line when no audio
        ctx!.fillStyle = '#000000';
        
        for (let x = dotSpacing; x < width; x += dotSpacing) {
          ctx!.beginPath();
          ctx!.arc(x, centerY, dotSize, 0, Math.PI * 2);
          ctx!.fill();
        }
      }
    };
    
    draw();
  }

  drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) {
    const r = Math.min(radius, height / 2, width / 2); // Avoid over-rounding
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop(); // triggers `onstop` where cleanup already happens
      this.isRecording = false;
      this.recordingFinished = true;
      this.recordingStatus = 'Not recording';
      this.submittingAudio = true;
      this.submitAudioQuestion();
    }

    // Ensure we stop the visualizer animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    // Close audio context if still open
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }

  closeRecording() {
    this.isRecording = false;
    this.recordingFinished = false;
    this.showEqualizer = false;
    this.audioUrl = null;
    this.base64Audio = null;
    this.audioBlob = undefined;
  
    // Stop playback if any
    this.audioRef.nativeElement.pause();
    this.audioRef.nativeElement.src = ''; // Fully unload audio
    this.audioRef.nativeElement.load();
  
    // Stop equalizer animation
    this.stopPlaybackEqualizer();
  
    // Disconnect mediaElementSource if exists
    if (this.mediaElementSource) {
      try {
        this.mediaElementSource.disconnect();
      } catch (e) {
        console.warn('mediaElementSource already disconnected or invalid', e);
      }
      this.mediaElementSource = null;
    }
  
    // Close audioContext if not already closed
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().then(() => {
        this.audioContext = null;
      });
    }
  }
  

  private convertToBase64(audioBlob: Blob) {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Remove the data URL prefix to get only the base64 data
      this.base64Audio = base64String.split(',')[1];
      this._changeDetect.detectChanges();
    };
    reader.readAsDataURL(audioBlob);
  }

  submitAudioQuestion() {
    // call api
    const fileName = this.generateUniqueFileName();
    let uploadedUrl: string | undefined;
    this._mediaService
      .getMediaUrls(fileName)
      .pipe(
        switchMap((response1) => {
          uploadedUrl = response1[0].get?.url;
          return this._mediaService.uploadMediaToS3(response1[0].put?.url!, this.audioBlob!);
        }),
        finalize(() => {})
      )
      .subscribe({
        next: (res) => {
          this.audioBlob = undefined;
          this._chatService.voiceToText(uploadedUrl);
        },
        error: (error) => {
          // this.isProcessing = false;
        }
      });
  }

  generateUniqueFileName(): string {
    const timestamp = Date.now();
    // Generate a random number
    const randomNum = Math.floor(Math.random() * 1e9);
    // Combine timestamp and random number to create a unique identifier
    const uniqueIdentifier = `${timestamp}-${randomNum}`;
    // Construct the unique filename with the file extension
    const uniqueFileName = `${uniqueIdentifier}.mp3`;
    return uniqueFileName;
  }


  loadingMoreChat() {
    this.loadingMoreHistory = true;
    this.currentChatPage++;
    if (this.isSharedChat) {
      this.getMessagesFromSharedId();
    } else {
      this.getMessagesFromId();
    }
  }

  resetChat(){
    this.loopMessages = [];
  }

  openSourcePanel(sources: any){
    this.selectedSources = sources;
    this.showSourceModal = true;
  }
  
  ngOnDestroy(): void {
    const playingIndex = this.loopMessages.findIndex(item => item.listening);
    if (playingIndex !== -1) {
      this.stopListening(playingIndex)
    }
    this._chatService.disconnectWebSocket();
    this._subscription.unsubscribe();
    this.messageSubscribed = false;
  }

}
